import { useObserver, useSubtree } from "subtree.js/react";
import { EbayGradeLinks } from "../../components/EbayGradeLinks";
import { ImageCarousel } from "../../components/ImageCarousel";
import { ImageLightbox } from "../../components/ImageLightbox";
import { ScanCandidates } from "../../components/ScanCandidates";
import { ScanEditForm } from "../../components/ScanEditForm";
import { StockTrackingModal } from "../../components/StockTrackingModal";
import { hasChoice, selectedProductId } from "../../core/primitives/candidates";
import { estimateForGrade, exactGradeKey } from "../../core/primitives/grades";
import { ScanEditorActions, ScanEditorState } from "./scan-editor-model";

/**
 * Reviewing one scan: correct the reading, then confirm it. Renders nothing
 * until the owning controller has seeded a scan.
 */
export function ScanEditor({
  isMobile,
  confirmLabel,
  cancelLabel,
  onCancel,
  showReject,
}: {
  isMobile: boolean;
  confirmLabel: string;
  cancelLabel: string;
  onCancel: () => void;
  /** Offer "Reject this scan" under the buttons (only meaningful while pending). */
  showReject: boolean;
}) {
  const state = useSubtree(ScanEditorState);
  const actions = useSubtree(ScanEditorActions);
  const observer = useObserver();

  const current = observer.watch(state.scan);
  const editState = observer.watch(state.edit);
  const busy = observer.watch(state.busy);
  const saving = observer.watch(state.saving);
  const draftError = observer.watch(state.draftError);
  const picking = observer.watch(state.picking);
  const saveError = observer.watch(state.error);
  const canConfirm = observer.watch(state.canConfirm);
  const confirmBlocker = observer.watch(state.confirmBlocker);
  const askingStock = observer.watch(state.askingStock);
  const zoomIndex = observer.watch(state.zoomIndex);
  const locations = observer.watch(state.locations);
  const quoteValidityMinutes = observer.watch(state.quoteValidityMinutes);

  if (!current || !editState) return null;

  const imageUrls = [current.imageUrl, current.backImageUrl].filter(Boolean) as string[];
  const pending = current.review === "PENDING";

  /**
   * What the last save did to the match, in one line.
   *
   * The match moving is the thing the reviewer cannot see for themselves and
   * the only reason the round trip exists — a correction that lands the scan on
   * a card the shop already has changes what confirming will do.
   */
  const matchNote =
    current.matchKind === "NONE"
      ? "Not in your catalogue — confirming creates a new card."
      : current.card?.confirmedAt == null
        ? "Matches a card nobody has reviewed yet."
        : "Matches a card already in your catalogue.";

  const editorActions = (
    <>
      {/* What the save is doing, and only while it is doing it. A correction
          that changed a field the pricing source was queried on costs a real
          lookup against a queue shared with another app, so "Repricing" is the
          honest word for the wait and "Saved" is the honest word for the rest.
          Silence here is what made a two-second pause look like a hang. */}
      <p className="mt-4 h-4 text-xs text-faint" aria-live="polite">
        {saving ? "Saving…" : draftError || !pending ? "" : matchNote}
      </p>
      {draftError && <p className="note note-warn mt-1 text-xs">{draftError}</p>}
      {saveError && <p className="note note-danger mt-4 text-danger">{saveError}</p>}
      {/* Only a customer's scan becomes a quote; confirming the shop's own scan holds nothing. */}
      {current.kind === "CUSTOMER" && (
        <p className="mt-4 text-xs text-faint">
          Approving quotes this price to the customer for {quoteValidityMinutes} minutes, and holds a copy for them
          until then.
        </p>
      )}
      {confirmBlocker && (
        <p id="confirm-blocker" className="field-error mt-4">
          {confirmBlocker}
        </p>
      )}
      <div className="mt-6 flex gap-3">
        <button type="button" onClick={onCancel} disabled={busy} className="btn-secondary flex-1">
          {cancelLabel}
        </button>
        <button
          type="button"
          onClick={actions.primary}
          disabled={busy || !canConfirm}
          aria-describedby={confirmBlocker ? "confirm-blocker" : undefined}
          className="btn-primary flex-1"
        >
          {busy ? "Confirming…" : confirmLabel}
        </button>
      </div>
      {pending && showReject && (
        <button type="button" onClick={() => void actions.reject()} disabled={busy} className="btn-ghost mt-3 w-full">
          Reject this scan
        </button>
      )}
      {askingStock && (
        <StockTrackingModal
          locations={[...locations]}
          busy={busy}
          onTrack={(stock) => void actions.confirm(stock)}
          onSkip={() => void actions.confirm()}
          onCancel={actions.cancelStock}
        />
      )}
    </>
  );

  // Which comp the grid marks. Computed from the grade **being edited** rather
  // than from `scan.estimatedPriceGrade`, so correcting the grade moves the mark
  // with it instead of leaving it on the comp the model's reading produced; the
  // two agree exactly while the reading stands.
  //
  // Only while the estimate is what is in the price field, though: a matched
  // card's own price came from the shop's earlier decision, not from a comp in
  // this grid, and pointing at one would credit it with a figure it did not
  // produce. Same rule as `estimatedFrom` below, and for the same reason.
  const pricedGradeKey =
    current.card?.currentPriceCents == null
      ? estimateForGrade(editState.grade, editState.slabbed, current.scpData).gradeKey
      : null;

  // The grade being edited, priced or not — so an ungraded card marks RAW even
  // when SCP has no loose price for it.
  const cardGradeKey = exactGradeKey(editState.grade, editState.slabbed);

  // Only when there is something to choose: the lookup's look-alikes, or a
  // list it declined to pick from. One settled product is just the form.
  const candidates = hasChoice(current.candidates, current.scpData) && (
    <div className="mb-5">
      <ScanCandidates
        candidates={current.candidates}
        selectedProductId={selectedProductId(current.scpData)}
        picking={picking}
        disabled={!pending || busy}
        onPick={(productId) => void actions.pick(productId)}
      />
    </div>
  );

  const form = (
    <ScanEditForm
      editState={editState}
      onChange={actions.change}
      matchedCard={current.matchKind === "PRODUCT" || current.matchKind === "IDENTITY" ? current.card : null}
      // Nothing was prefilled because the source had no figure for this grade,
      // which the reviewer needs told rather than left to infer from a blank.
      noEstimate={current.estimatedPriceCents === null && current.card?.currentPriceCents == null}
      // Only meaningful while the estimate is what is in the field: once a
      // matched card's own price seeds it, the provenance is that card's.
      estimatedFrom={current.card?.currentPriceCents == null ? current.estimatedPriceFrom : null}
      scpData={current.scpData}
    />
  );

  if (isMobile) {
    return (
      <div className="mx-auto max-w-sm">
        <ImageCarousel urls={imageUrls} />
        <h2 className="mb-4 text-base font-semibold text-white">Review scan</h2>
        {candidates}
        {form}
        <div className="mt-6">
          <EbayGradeLinks
            searchLinks={current.searchLinks ?? undefined}
            scpData={current.scpData}
            pricedGradeKey={pricedGradeKey}
            cardGradeKey={cardGradeKey}
          />
        </div>
        {editorActions}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl">
      <div className="mb-6 flex items-start gap-5">
        {/* Big enough to read a slab label at a glance, and a button because
            even this is not enough to judge a corner or a centring call — which
            is the decision confirming actually asks for. */}
        <div className="flex shrink-0 gap-3">
          {imageUrls.map((url, i) => (
            <button
              key={url}
              type="button"
              onClick={() => actions.openZoom(i)}
              aria-label={`Enlarge the ${i === 0 ? "front" : "back"} photo`}
              className="group relative h-56 w-40 shrink-0 overflow-hidden rounded-row border border-line bg-surface"
            >
              <img src={url} alt="" className="h-full w-full object-contain" />
              <span className="absolute inset-x-0 bottom-0 bg-black/70 py-1 text-center text-[11px] text-muted opacity-0 transition-opacity group-hover:opacity-100">
                Click to enlarge
              </span>
            </button>
          ))}
        </div>
        <div>
          <h2 className="mb-1 text-lg font-semibold text-white">Review scan</h2>
          <p className="text-sm text-muted">Correct what the scan read, then confirm it.</p>
        </div>
      </div>
      {candidates}
      {form}
      <div className="mt-6">
        <EbayGradeLinks
          searchLinks={current.searchLinks ?? undefined}
          scpData={current.scpData}
          pricedGradeKey={pricedGradeKey}
          cardGradeKey={cardGradeKey}
        />
      </div>
      {editorActions}
      {zoomIndex !== null && <ImageLightbox urls={imageUrls} startIndex={zoomIndex} onClose={actions.closeZoom} />}
    </div>
  );
}
